import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { FormConfigSectionOptions } from '../../../interfaces/form-config.interface';
import { getErrorMsg } from '../../../utils/error';

interface SectionOptionsTabProps {
  sectionOptions: FormConfigSectionOptions;
  onAddSectionOption: (type: 'listing' | 'detail', value: string) => Promise<void>;
}

export const SectionOptionsTab = ({
  sectionOptions,
  onAddSectionOption,
}: SectionOptionsTabProps) => {
  const [newSectionType, setNewSectionType] = useState<'listing' | 'detail'>('listing');
  const [newSectionValue, setNewSectionValue] = useState('');
  const [addingOption, setAddingOption] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const handleAdd = async () => {
    const trimmed = newSectionValue.trim();
    if (!trimmed) return;
    setAddingOption(true);
    setAddError(null);
    try {
      await onAddSectionOption(newSectionType, trimmed);
      setNewSectionValue('');
    } catch (err: unknown) {
      setAddError(getErrorMsg(err, 'Failed to add option'));
    } finally {
      setAddingOption(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          Manage Section Options
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Add the section names available in the Listing and Detail dropdowns.
        </p>
      </div>

      {/* Add form */}
      <div className="flex gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Type</label>
          <select
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            value={newSectionType}
            onChange={(e) => setNewSectionType(e.target.value as 'listing' | 'detail')}
          >
            <option value="listing">Listing</option>
            <option value="detail">Detail</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">
            Value (e.g. Custom_Section)
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            placeholder="Section_Name"
            value={newSectionValue}
            onChange={(e) => setNewSectionValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={!newSectionValue.trim() || addingOption}
          onClick={handleAdd}
        >
          {addingOption ? '…' : '+ Add'}
        </Button>
      </div>

      {addError && <p className="text-xs text-red-500">{addError}</p>}

      {/* Two-column display of existing options */}
      <div className="grid grid-cols-2 gap-6">
        {(['listing', 'detail'] as const).map((type) => (
          <div key={type}>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 capitalize">
              {type} Sections
            </p>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {sectionOptions[type].length === 0 ? (
                <p className="text-xs text-gray-400 italic px-1">None yet</p>
              ) : (
                sectionOptions[type].map((opt) => (
                  <div
                    key={opt.id}
                    className="px-2 py-1 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                  >
                    <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                      {opt.value}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionOptionsTab;
